/**
 * Wraps BountyDisplay in a SubheadedPage header (bounty title + formatted
 * category/sex/type subheading). type "owner" adds a delete button with
 * confirm AlertDialog that calls UserBountyAPI.deleteBounty and redirects to
 * "/account/listings"; type "public" is display-only. Reads the bounty from
 * location.state. Rendered as "owner" by BountySubmissionsPage
 * ("/account/listings/:bountyId") and BountyAcceptedSubmissionPage, and as
 * "public" inside SubmissionBountyDisplay ("/account/submissions/:submissionId").
 */
import BountyDisplay from "@/pages/bounty/BountyDisplay";
import { SubheadedPage } from "@/components/SubheadedPage";
import { Bounty } from "@/types/bounty";
import { useLocation } from "react-router-dom";
import { formatBountyCategorySexCategoryType } from "@/utils/bounty";
import { Trash } from "lucide-react";
import { useState } from "react";
import { UserBountyAPI } from "@/api/userBounty";
import { useNavigate } from "react-router-dom";
import { useUserState } from "@/contexts/UserContext";
import AlertDialog from "@/components/AlertDialog";
import { useError } from "@/contexts/ErrorContext";

export type SubheadedBountyDisplayType = "owner" | "public";

type SubheadedBountyDisplayProps = {
    type: SubheadedBountyDisplayType;
};

const SubheadedBountyDisplay: React.FC<SubheadedBountyDisplayProps> = ({ type }) => {
    const location = useLocation();
    const navigate = useNavigate();
    const userState = useUserState();
    const bounty = (location.state.bounty || location.state.submission!.bounty) as Bounty;
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const { showError } = useError();

    const handleDeleteClick = async () => {
        if (type === "public") {
            throw new Error("Cannot delete bounty in public mode");
        }
        try {
            await UserBountyAPI.deleteBounty(userState!.id, bounty.id!);
            navigate("/account/listings");
        } catch (error: any) {
            showError(error as string);
        }
    };

    return (
        <SubheadedPage
            heading={bounty.title}
            subheading={formatBountyCategorySexCategoryType(bounty)}
            headerAction={
                type === "owner" &&
                bounty.status === "active" && (
                    <div className="w-full relative">
                        <div className="absolute top-[-24px] right-0 flex flex-row space-x-4">
                            <button
                                className={`p-2 rounded-full hover:bg-slate-100 transition-colors`}
                                onClick={() => setShowDeleteDialog(true)}
                            >
                                <Trash size={25} />
                            </button>
                        </div>
                    </div>
                )
            }
        >
            <BountyDisplay />
            {showDeleteDialog && (
                <AlertDialog
                    title="delete bounty"
                    message="are you sure you want to delete this bounty?"
                    cancelText="not now"
                    confirmText="ok"
                    onConfirm={() => {
                        handleDeleteClick();
                        setShowDeleteDialog(false);
                    }}
                    onCancel={() => {
                        setShowDeleteDialog(false);
                    }}
                />
            )}
        </SubheadedPage>
    );
};

export default SubheadedBountyDisplay;
